/**
 * 🎯 useOperationsCenterRibbon — Ribbon for Operations Center
 *
 * Groups: Execução | Controle | Visual
 */

import { useMemo } from 'react';
import {
  RefreshCw, Bell, BellOff, Maximize2, Minimize2,
  AlertTriangle, BarChart3, List, LayoutGrid,
  Play, Pause, CheckCircle2, ArrowUpRight, UserCheck,
  RotateCcw, Clock, Filter,
} from 'lucide-react';
import type { RibbonConfig } from '../types/ribbon';

const NOOP = () => {};

export interface OperationsCenterRibbonDeps {
  // Execução
  onAssume?: () => void;
  onStart?: () => void;
  onPause?: () => void;
  onFinish?: () => void;
  onEscalate?: () => void;
  hasSelection?: boolean;
  // Controle
  onRefresh?: () => void;
  onReopen?: () => void;
  onToggleAlerts?: () => void;
  showAlerts?: boolean;
  alertCount?: number;
  onFilter?: () => void;
  hasActiveFilters?: boolean;
  // Visual
  onToggleWallboard?: () => void;
  isWallboard?: boolean;
  viewMode?: 'list' | 'compact' | 'indicators';
  onViewModeChange?: (mode: 'list' | 'compact' | 'indicators') => void;
  afterGroups?: React.ReactNode;
}

export function useOperationsCenterRibbon(deps: OperationsCenterRibbonDeps): RibbonConfig {
  const sel = deps.hasSelection ?? false;

  return useMemo<RibbonConfig>(() => ({
    moduleId: 'operations',
    groups: [
      // ─── Execução (LEFT) ─────────────────────────
      {
        id: 'execucao',
        label: 'Execução',
        position: 'left',
        actions: [
          { id: 'assume', label: 'Assumir', icon: UserCheck, onClick: deps.onAssume ?? NOOP, disabled: !sel, hidden: !deps.onAssume },
          { id: 'start', label: 'Iniciar', icon: Play, onClick: deps.onStart ?? NOOP, disabled: !sel, variant: 'success', hidden: !deps.onStart },
          { id: 'pause', label: 'Pausar', icon: Pause, onClick: deps.onPause ?? NOOP, disabled: !sel, hidden: !deps.onPause },
          { id: 'finish', label: 'Finalizar', icon: CheckCircle2, onClick: deps.onFinish ?? NOOP, disabled: !sel, variant: 'primary', hidden: !deps.onFinish },
          { id: 'escalate', label: 'Escalar', icon: ArrowUpRight, onClick: deps.onEscalate ?? NOOP, disabled: !sel, variant: 'danger', hidden: !deps.onEscalate },
        ],
      },
      // ─── Controle (LEFT) ─────────────────────────
      {
        id: 'controle',
        label: 'Controle',
        position: 'left',
        actions: [
          { id: 'refresh', label: 'Atualizar', icon: RefreshCw, onClick: deps.onRefresh ?? NOOP },
          { id: 'reopen', label: 'Reabrir', icon: RotateCcw, onClick: deps.onReopen ?? NOOP, disabled: !sel, hidden: !deps.onReopen },
          {
            id: 'alerts',
            label: 'Alertas',
            icon: deps.showAlerts ? BellOff : Bell,
            onClick: deps.onToggleAlerts ?? NOOP,
            active: deps.showAlerts,
            badge: deps.alertCount && deps.alertCount > 0 ? deps.alertCount : undefined,
          },
        ],
      },
      // ─── Visual (LEFT) ──────────────────────────
      {
        id: 'visual',
        label: 'Visual',
        position: 'left',
        actions: [
          {
            id: 'view-list',
            label: 'Lista',
            icon: List,
            onClick: () => deps.onViewModeChange?.('list'),
            active: deps.viewMode === 'list' || !deps.viewMode,
          },
          {
            id: 'view-compact',
            label: 'Compacto',
            icon: LayoutGrid,
            onClick: () => deps.onViewModeChange?.('compact'),
            active: deps.viewMode === 'compact',
          },
          {
            id: 'view-indicators',
            label: 'Indicadores',
            icon: BarChart3,
            onClick: () => deps.onViewModeChange?.('indicators'),
            active: deps.viewMode === 'indicators',
          },
          {
            id: 'wallboard',
            label: deps.isWallboard ? 'Normal' : 'Wallboard',
            icon: deps.isWallboard ? Minimize2 : Maximize2,
            onClick: deps.onToggleWallboard ?? NOOP,
            active: deps.isWallboard,
          },
        ],
      },
    ],
    slots: {
      afterGroups: deps.afterGroups,
    },
  }), [
    sel, deps.onAssume, deps.onStart, deps.onPause, deps.onFinish, deps.onEscalate,
    deps.onRefresh, deps.onReopen, deps.onToggleAlerts, deps.showAlerts, deps.alertCount,
    deps.onFilter, deps.hasActiveFilters,
    deps.onToggleWallboard, deps.isWallboard, deps.viewMode, deps.onViewModeChange,
    deps.afterGroups,
  ]);
}
